import mapboxgl from 'mapbox-gl'
import { type RefObject, useEffect, useRef } from 'react'
import {
	ID_CLUSTERS_LAYER,
	ID_DATA_SOURCE,
	ID_MARKERS_LAYER,
	INITIAL_CENTER,
	ZOOM_MARKER,
	ZOOM_MAX,
	ZOOM_MIN,
} from '../constants'
import { createFeatureCollection } from '../helpers'
import type { MapInit } from '../types'

const token = process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? ''
const styleUrl = process.env.NEXT_PUBLIC_MAPBOX_STYLE_URL ?? ''

export default function useMap(container: RefObject<HTMLElement | null>, options?: MapInit) {
	const mapInstance = useRef<mapboxgl.Map | null>(null)
	const initOptions = useRef(options)

	useEffect(() => {
		const el = container.current
		if (!el || mapInstance.current) return

		// Create map instance
		const init = initOptions.current
		const map = new mapboxgl.Map({
			accessToken: token,
			container: el,
			style: styleUrl,
			projection: 'mercator',
			logoPosition: 'top-right',
			minZoom: ZOOM_MIN,
			maxZoom: ZOOM_MAX,
			...(init?.marker
				? {
						zoom: ZOOM_MARKER,
						bounds: new mapboxgl.LngLatBounds().extend([init.marker.lng, init.marker.lat]),
						...(init.markersContainer && {
							fitBoundsOptions: {
								zoom: ZOOM_MARKER,
							},
						}),
					}
				: {
						zoom: ZOOM_MIN,
						center: INITIAL_CENTER,
					}),
		})

		// Disable unwanted interactions
		map.dragRotate.disable()
		// map.touchZoomRotate.disable()
		map.touchPitch.disable()

		// Create sources and layers
		map.once('load', () => {
			map
				.addSource(ID_DATA_SOURCE, {
					type: 'geojson',
					cluster: true,
					clusterRadius: 30,
					data: createFeatureCollection([]),
				})
				.addLayer({
					id: ID_CLUSTERS_LAYER,
					type: 'symbol',
					source: ID_DATA_SOURCE,
					filter: ['has', 'point_count'],
				})
				.addLayer({
					id: ID_MARKERS_LAYER,
					type: 'symbol',
					source: ID_DATA_SOURCE,
					filter: ['!', ['has', 'point_count']],
				})
		})

		// Handle instance lifecycle
		mapInstance.current = map
		return () => {
			map.remove()
			mapInstance.current = null
		}
	}, [container])

	return mapInstance
}
